import { useState, useEffect } from "react";
import type { Stripe } from "@stripe/stripe-js";

interface UsePaymentMethodsLoadingProps {
  stripe: Stripe | null;
  amount: string;
}

export const usePaymentMethodsLoading = ({
  stripe,
  amount,
}: UsePaymentMethodsLoadingProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showApplePay, setShowApplePay] = useState(false);
  const [showGooglePay, setShowGooglePay] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!stripe || !amount) return;

    const checkPaymentMethods = async () => {
      try {
        setIsLoading(true);
        const amountNum = Math.round(parseFloat(amount) * 100);

        const paymentRequest = stripe.paymentRequest({
          country: "US",
          currency: "usd",
          total: { label: "Total", amount: amountNum },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        const result = await paymentRequest.canMakePayment();
        const hasApplePay = !!(result && result.applePay);
        const hasGooglePay = !!(result && result.googlePay);

        setShowApplePay(hasApplePay);
        setShowGooglePay(hasGooglePay);
        setIsInitialized(true);
      } catch (error) {
        console.error("Error checking payment methods:", error);
        setShowApplePay(false);
        setShowGooglePay(false);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentMethods();
  }, [stripe, amount]);

  return {
    isLoading,
    showApplePay,
    showGooglePay,
    isInitialized,
  };
};
